/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { Channel } from '../entities/Channel';
import { LiveTvError } from '../error';
import { PlaybackContentType } from '../playback/enums';
import { ITivoSubscription, TivoSubscriptionModel } from '../tivo';
import { TChannelV2 } from '../types';
import { MetadataState, MetadataType } from './enums';
import { NdvrMetaDataModelImpl } from './impls';
import { LinearMetadataModelImpl } from './impls/LinearMetadataModelImpl';
import { SocuMetadataModelImpl } from './impls/SocuMetadataModelImpl';
import { VodMetaDataModelImpl } from './impls/VodMetadataModelImpl';
import {
    IMetadata,
    IMetadataManager,
    IMetadataModel,
    INdvrPlaybackModel,
    IPlayable,
    IVodPlaybackModel,
} from './interfaces';

/* eslint-disable class-methods-use-this */
/* eslint-disable no-unused-vars */
export class MetadataManager implements IMetadataManager {
    private mMetadataModel: IMetadataModel;

    private mMetadataState: MetadataState;

    private oTivoSubscription: ITivoSubscription<MetadataState>;

    public constructor() {
        this.mMetadataState = MetadataState.NONE;
        this.oTivoSubscription = new TivoSubscriptionModel<MetadataState>();
        this.mMetadataModel = new LinearMetadataModelImpl();
    }

    private async deleteRecording(ndvrPlaybackModel: INdvrPlaybackModel) {
        let state = MetadataState.PREPARING;
        try {
            await this.mMetadataModel.prepareMetadataByType({
                metadataType: MetadataType.DELETE_RECORDING,
                ndvrPlaybackModel,
            });
            state = MetadataState.DELETE_RECORDING_DONE;
        } catch (error) {
            state = MetadataState.DELETE_RECORDING_ERROR;
        } finally {
            this.setMetadataState(state);
        }
    }

    private async retrieveActionDetails(channel: TChannelV2 | Channel) {
        let state = MetadataState.PREPARING;
        try {
            await this.mMetadataModel.prepareMetadataByType({
                metadataType: MetadataType.ACTION_DETAILS,
                channel,
            });
            state = MetadataState.ACTION_DETAILS_READY;
        } catch (error) {
            state = MetadataState.ACTION_DETAILS_ERROR;
        } finally {
            this.setMetadataState(state);
        }
    }

    private async retrieveActionList(channel: TChannelV2 | Channel) {
        let state = MetadataState.PREPARING;
        try {
            await this.mMetadataModel.prepareMetadataByType({
                metadataType: MetadataType.ACTION_LIST,
                channel,
            });
            state = MetadataState.ACTION_LIST_READY;
        } catch (error) {
            state = MetadataState.ACTION_LIST_ERROR;
        } finally {
            this.setMetadataState(state);
        }
    }

    private async retrieveNdvrActionList(ndvrPlaybackModel: INdvrPlaybackModel) {
        let state = MetadataState.PREPARING;
        try {
            await this.mMetadataModel.prepareMetadataByType({
                metadataType: MetadataType.ACTION_LIST,
                ndvrPlaybackModel,
            });
            state = MetadataState.ACTION_LIST_READY;
        } catch (error) {
            state = MetadataState.ACTION_LIST_ERROR;
        } finally {
            this.setMetadataState(state);
        }
    }

    private async retrieveNdvrFullPreview(ndvrPlaybackModel: INdvrPlaybackModel) {
        let state = MetadataState.PREPARING;
        try {
            await this.mMetadataModel.prepareMetadataByType({
                metadataType: MetadataType.FULL_INFO_PREVIEW,
                ndvrPlaybackModel,
            });
            state = MetadataState.RECORDING_PREVIEW_READY;
        } catch (error) {
            state = MetadataState.RECORDING_PREVIEW_ERROR;
        } finally {
            this.setMetadataState(state);
        }
    }

    private async retrieveNdvrLastViewedPosition(ndvrPlaybackModel: INdvrPlaybackModel) {
        let state = MetadataState.PREPARING;
        try {
            await this.mMetadataModel.prepareMetadataByType({
                metadataType: MetadataType.NDVR_LAST_VIEWED_POSITION,
                ndvrPlaybackModel,
            });
            state = MetadataState.NDVR_LAST_VIEWED_POSITION_READY;
        } catch (error) {
            state = MetadataState.NDVR_LAST_VIEWED_POSITION_ERROR;
        } finally {
            this.setMetadataState(state);
        }
    }

    private async retrieveNdvrPreview(ndvrPlaybackModel: INdvrPlaybackModel) {
        let state = MetadataState.PREPARING;
        try {
            await this.mMetadataModel.prepareMetadataByType({
                metadataType: MetadataType.PREVIEW,
                ndvrPlaybackModel,
            });
            state = MetadataState.RECORDING_PREVIEW_READY;
        } catch (error) {
            state = MetadataState.RECORDING_PREVIEW_ERROR;
        } finally {
            this.setMetadataState(state);
        }
    }

    private async retrieveNdvrRecordingActionList(ndvrPlaybackModel: INdvrPlaybackModel) {
        let state = MetadataState.PREPARING;
        try {
            await this.mMetadataModel.prepareMetadataByType({
                metadataType: MetadataType.RECORDING_ACTION_LIST,
                ndvrPlaybackModel,
            });
            state = MetadataState.RECORDING_ACTION_READY;
        } catch (error) {
            state = MetadataState.RECORDING_ACTION_ERROR;
        } finally {
            this.setMetadataState(state);
        }
    }

    private async retrieveNdvrRecordingInitActionList(ndvrPlaybackModel: INdvrPlaybackModel) {
        let state = MetadataState.PREPARING;
        try {
            await this.mMetadataModel.prepareMetadataByType({
                metadataType: MetadataType.RECORDING_ACTION_LIST,
                ndvrPlaybackModel,
            });
            state = MetadataState.RECORDING_ACTION_INIT_READY;
        } catch (error) {
            state = MetadataState.RECORDING_ACTION_ERROR;
        } finally {
            this.setMetadataState(state);
        }
    }

    private async retrieveSocuFullPreview(channel: TChannelV2 | Channel) {
        let state = MetadataState.PREPARING;
        try {
            await this.mMetadataModel.prepareMetadataByType({
                metadataType: MetadataType.FULL_INFO_PREVIEW,
                channel,
            });
            state = MetadataState.VOD_PREVIEW_READY;
        } catch (error) {
            state = MetadataState.VOD_PREVIEW_ERROR;
        } finally {
            this.setMetadataState(state);
        }
    }

    private async retrieveSocuPreview(channel: TChannelV2 | Channel) {
        let state = MetadataState.PREPARING;
        try {
            await this.mMetadataModel.prepareMetadataByType({
                metadataType: MetadataType.PREVIEW,
                channel,
            });
            state = MetadataState.VOD_PREVIEW_READY;
        } catch (error) {
            state = MetadataState.VOD_PREVIEW_ERROR;
        } finally {
            this.setMetadataState(state);
        }
    }

    private async retrieveSocuActionList(channel: TChannelV2 | Channel) {
        let state = MetadataState.PREPARING;
        try {
            await this.mMetadataModel.prepareMetadataByType({
                metadataType: MetadataType.VOD_ACTION_LIST,
                channel,
            });
            state = MetadataState.VOD_ACTION_READY;
        } catch (error) {
            state = MetadataState.VOD_ACTION_ERROR;
        } finally {
            this.setMetadataState(state);
        }
    }

    private async retrievePlayNextUnifiedCollection(
        playNextUnifiedCollectionParams?:
            | TChannelV2
            | Channel
            | INdvrPlaybackModel
            | IVodPlaybackModel,
    ) {
        let state = MetadataState.PREPARING;
        try {
            await this.mMetadataModel.prepareMetadataByType({
                metadataType: MetadataType.PLAY_NEXT_UNIFIED_COLLECTION,
                ...playNextUnifiedCollectionParams,
            } as any);
            state = MetadataState.PLAY_NEXT_COLLECTION_READY;
        } catch (error) {
            state = MetadataState.PLAY_NEXT_COLLECTION_ERROR;
        } finally {
            this.setMetadataState(state);
        }
    }

    private async retrievePlayNextPreview(
        playNextPreviewParams?: TChannelV2 | Channel | INdvrPlaybackModel | IVodPlaybackModel,
    ) {
        let state = MetadataState.PREPARING;
        try {
            await this.mMetadataModel.prepareMetadataByType({
                metadataType: MetadataType.PLAY_NEXT_PREVIEW,
                ...playNextPreviewParams,
            } as any);
            state = MetadataState.PLAY_NEXT_PREVIEW_READY;
        } catch (error) {
            state = MetadataState.PLAY_NEXT_PREVIEW_ERROR;
        } finally {
            this.setMetadataState(state);
        }
    }

    private async retrievePlayNextActions(
        playNextActionParams?: TChannelV2 | Channel | INdvrPlaybackModel | IVodPlaybackModel,
    ) {
        let state = MetadataState.PREPARING;
        try {
            await this.mMetadataModel.prepareMetadataByType({
                metadataType: MetadataType.PLAY_NEXT_ACTIONS,
                ...playNextActionParams,
            } as any);
            state = MetadataState.PLAY_NEXT_ACTIONS_READY;
        } catch (error) {
            state = MetadataState.PLAY_NEXT_ACTIONS_ERROR;
        } finally {
            this.setMetadataState(state);
        }
    }

    private async retrieveVodActionDetails() {
        let state = MetadataState.PREPARING;
        try {
            await this.mMetadataModel.prepareMetadataByType({
                metadataType: MetadataType.ACTION_DETAILS,
            });
            state = MetadataState.ACTION_DETAILS_READY;
        } catch (error) {
            state = MetadataState.ACTION_DETAILS_ERROR;
        } finally {
            this.setMetadataState(state);
        }
    }

    private async retrieveVodFullPreview() {
        let state = MetadataState.PREPARING;
        try {
            await this.mMetadataModel.prepareMetadataByType({
                metadataType: MetadataType.FULL_INFO_PREVIEW,
            });
            state = MetadataState.VOD_PREVIEW_READY;
        } catch (error) {
            state = MetadataState.VOD_PREVIEW_ERROR;
        } finally {
            this.setMetadataState(state);
        }
    }

    private async retrieveVodPreview() {
        let state = MetadataState.PREPARING;
        try {
            await this.mMetadataModel.prepareMetadataByType({
                metadataType: MetadataType.PREVIEW,
            });
            state = MetadataState.VOD_PREVIEW_READY;
        } catch (error) {
            state = MetadataState.VOD_PREVIEW_ERROR;
        } finally {
            this.setMetadataState(state);
        }
    }

    private async retrieveVodActions() {
        let state = MetadataState.PREPARING;
        try {
            await this.mMetadataModel.prepareMetadataByType({
                metadataType: MetadataType.VOD_ACTION_LIST,
            });
            state = MetadataState.VOD_ACTION_READY;
        } catch (error) {
            state = MetadataState.VOD_ACTION_ERROR;
        } finally {
            this.setMetadataState(state);
        }
    }

    private async prepareLiveMetadata(channel: TChannelV2 | Channel, playerCurrentTime?: number) {
        let state = MetadataState.PREPARING;
        try {
            await this.mMetadataModel.prepareMetadataByType({
                metadataType: MetadataType.AIRING,
                channel,
                playerCurrentTime,
            });
            state = MetadataState.AIRING_READY;
        } catch (error) {
            state = MetadataState.AIRING_ERROR;
        } finally {
            this.setMetadataState(state);
        }
        if (state === MetadataState.AIRING_ERROR) return;
        try {
            await this.mMetadataModel.prepareMetadataByType({
                metadataType: MetadataType.PREVIEW,
                channel,
                playerCurrentTime,
            });
            state = MetadataState.PREVIEW_READY;
        } catch (error) {
            state = MetadataState.PREVIEW_ERROR;
        } finally {
            this.setMetadataState(state);
        }
        try {
            await this.mMetadataModel.prepareMetadataByType({
                metadataType: MetadataType.ACTION_LIST,
                channel,
                playerCurrentTime,
            });
            state = MetadataState.ACTION_LIST_READY;
        } catch (error) {
            state = MetadataState.ACTION_LIST_ERROR;
        } finally {
            this.setMetadataState(state);
        }
    }

    private async prepareLiveMetadataByType(
        metadataType: MetadataType,
        channel: TChannelV2 | Channel,
        playerCurrentTime?: number,
    ) {
        switch (metadataType) {
            case MetadataType.ACTION_LIST:
                this.retrieveActionList(channel);
                break;
            default:
                this.prepareLiveMetadata(channel, playerCurrentTime);
        }
    }

    private async prepareNdvrMetadata(ndvrPlaybackModel: INdvrPlaybackModel) {
        await this.retrieveNdvrActionList(ndvrPlaybackModel);
        if (this.getMetadataState() === MetadataState.ACTION_LIST_ERROR) return;
        this.retrieveNdvrRecordingInitActionList(ndvrPlaybackModel);
        this.retrieveNdvrPreview(ndvrPlaybackModel);
        this.retrieveNdvrLastViewedPosition(ndvrPlaybackModel);
    }

    private async prepareNdvrMetadataByType(
        metadataType: MetadataType,
        ndvrPlaybackModel: INdvrPlaybackModel,
    ) {
        switch (metadataType) {
            case MetadataType.DELETE_RECORDING:
                this.deleteRecording(ndvrPlaybackModel);
                break;
            case MetadataType.FULL_INFO_PREVIEW:
                this.retrieveNdvrFullPreview(ndvrPlaybackModel);
                break;
            case MetadataType.NDVR_LAST_VIEWED_POSITION:
                this.retrieveNdvrLastViewedPosition(ndvrPlaybackModel);
                break;
            case MetadataType.PREVIEW:
                this.retrieveNdvrPreview(ndvrPlaybackModel);
                break;
            case MetadataType.RECORDING_ACTION_LIST:
                this.retrieveNdvrRecordingActionList(ndvrPlaybackModel);
                break;
            case MetadataType.PLAY_NEXT_UNIFIED_COLLECTION:
                this.retrievePlayNextUnifiedCollection(ndvrPlaybackModel);
                break;
            case MetadataType.PLAY_NEXT_PREVIEW:
                this.retrievePlayNextPreview(ndvrPlaybackModel);
                break;
            case MetadataType.PLAY_NEXT_ACTIONS:
                this.retrievePlayNextActions(ndvrPlaybackModel);
                break;
            default:
                this.retrieveNdvrActionList(ndvrPlaybackModel);
        }
    }

    private async prepareSocuMetadata(channel: TChannelV2 | Channel) {
        this.retrieveActionList(channel);
        if (this.getMetadataState() === MetadataState.ACTION_LIST_ERROR) return;
        this.retrieveActionDetails(channel);
        if (this.getMetadataState() === MetadataState.ACTION_DETAILS_ERROR) return;
        this.retrieveSocuPreview(channel);
    }

    private async prepareSocuMetadataByType(
        metadataType: MetadataType,
        channel: TChannelV2 | Channel,
    ) {
        switch (metadataType) {
            case MetadataType.ACTION_DETAILS:
                this.retrieveActionDetails(channel);
                break;
            case MetadataType.PREVIEW:
                this.retrieveSocuPreview(channel);
                break;
            case MetadataType.FULL_INFO_PREVIEW:
                this.retrieveSocuFullPreview(channel);
                break;
            case MetadataType.VOD_ACTION_LIST:
                this.retrieveSocuActionList(channel);
                break;
            case MetadataType.PLAY_NEXT_UNIFIED_COLLECTION:
                this.retrievePlayNextUnifiedCollection(channel);
                break;
            case MetadataType.PLAY_NEXT_PREVIEW:
                this.retrievePlayNextPreview(channel);
                break;
            case MetadataType.PLAY_NEXT_ACTIONS:
                this.retrievePlayNextActions(channel);
                break;
            default:
                this.retrieveActionList(channel);
        }
    }

    private async prepareVodMetadata() {
        this.retrieveVodActionDetails();
        if (this.getMetadataState() === MetadataState.VOD_ACTION_ERROR) return;
        this.retrieveVodPreview();
    }

    private async prepareVodMetadataByType(
        metadataType: MetadataType,
        vodPlaybackModel: IVodPlaybackModel,
    ) {
        switch (metadataType) {
            case MetadataType.ACTION_DETAILS:
                this.retrieveVodActionDetails();
                break;
            case MetadataType.FULL_INFO_PREVIEW:
                this.retrieveVodFullPreview();
                break;
            case MetadataType.PREVIEW:
                this.retrieveVodPreview();
                break;
            case MetadataType.VOD_ACTION_LIST:
                this.retrieveVodActions();
                break;
            case MetadataType.PLAY_NEXT_UNIFIED_COLLECTION:
                this.retrievePlayNextUnifiedCollection(vodPlaybackModel);
                break;
            case MetadataType.PLAY_NEXT_PREVIEW:
                this.retrievePlayNextPreview(vodPlaybackModel);
                break;
            case MetadataType.PLAY_NEXT_ACTIONS:
                this.retrievePlayNextActions(vodPlaybackModel);
                break;
            default:
        }
    }

    private setMetadataState(metadataState: MetadataState) {
        this.mMetadataState = metadataState;
        this.oTivoSubscription.notify(metadataState);
    }

    public getMetadata(): IMetadata {
        return this.mMetadataModel.getMetadata();
    }

    public getMetadataError(): LiveTvError {
        return this.mMetadataModel.getError();
    }

    public getMetadataState(): MetadataState {
        return this.mMetadataState;
    }

    public getMetadataStateChangeListener(): ITivoSubscription<MetadataState> {
        return this.oTivoSubscription;
    }

    public prepareMetadata(playable: IPlayable): void {
        switch (playable.playbackContentType) {
            case PlaybackContentType.LIVE:
                if (!playable.channel) return;
                this.mMetadataModel = new LinearMetadataModelImpl();
                this.prepareLiveMetadata(playable.channel);
                break;

            case PlaybackContentType.NDVR_LIVE:
            case PlaybackContentType.NDVR_RECORDED:
                if (!playable.ndvrPlaybackModel) return;
                this.mMetadataModel = new NdvrMetaDataModelImpl();
                this.prepareNdvrMetadata(playable.ndvrPlaybackModel);
                break;

            case PlaybackContentType.CATCHUP_BEGINNING:
            case PlaybackContentType.CATCHUP_RESUME:
            case PlaybackContentType.STARTOVER_BEGINNING:
            case PlaybackContentType.STARTOVER_RESUME:
                if (!playable.channel) return;
                this.mMetadataModel = new SocuMetadataModelImpl();
                this.prepareSocuMetadata(playable.channel);
                break;

            case PlaybackContentType.VOD:
                this.mMetadataModel = new VodMetaDataModelImpl();
                this.prepareVodMetadata();
                break;
            default:
                break;
        }
    }

    public prepareMetadataByType(
        playable: IPlayable,
        metadataType: MetadataType,
        playerCurrentTime?: number,
    ): void {
        switch (playable.playbackContentType) {
            case PlaybackContentType.LIVE:
                if (!playable.channel) return;
                this.prepareLiveMetadataByType(metadataType, playable.channel, playerCurrentTime);
                break;
            case PlaybackContentType.NDVR_LIVE:
            case PlaybackContentType.NDVR_RECORDED:
                if (!playable.ndvrPlaybackModel) return;
                this.prepareNdvrMetadataByType(metadataType, playable.ndvrPlaybackModel);
                break;
            case PlaybackContentType.CATCHUP_BEGINNING:
            case PlaybackContentType.CATCHUP_RESUME:
            case PlaybackContentType.STARTOVER_BEGINNING:
            case PlaybackContentType.STARTOVER_RESUME:
                if (!playable.channel) return;
                this.prepareSocuMetadataByType(metadataType, playable.channel);
                break;
            case PlaybackContentType.VOD:
                if (!playable.vodPlaybackModel) return;
                this.prepareVodMetadataByType(metadataType, playable.vodPlaybackModel);
                break;
            default:
                break;
        }
    }
}
